/**
 * Reading a team for display.
 *
 * A team reaches the start step in one of two shapes: the trimmed
 * `{ id, name, image }` nested inside each division on `GET /registration/:id`,
 * or the fuller object the team endpoints return, which adds roster counts, a
 * bucket flag, and an image OBJECT rather than a URL string.
 *
 * These helpers read whichever arrived. Everything past the name is optional,
 * and absent is reported as "unknown" rather than being flattened to zero — a
 * team with no roster count is not a team with no players.
 */

import type { StartTeam } from './types'

/**
 * The id a team is addressed by.
 *
 * The registration payload sends `uid` and `id` carrying the same value, but
 * the trimmed nested shape has only `id` — so prefer `uid` and fall back,
 * rather than assuming either is always there. Returns undefined for a team
 * with neither, which cannot be linked to or indexed.
 */
export const teamIdentity = (
  team: StartTeam | null | undefined
): string | undefined => team?.uid ?? team?.id ?? undefined

/**
 * Whether a team is the one a `?team_id=` link names.
 *
 * Accepts EITHER key, because a link is built from whichever the builder had.
 * Deliberately NOT `team_player_roster_id`: that is a roster row id, a
 * different namespace from the team id, and matching it could resolve a
 * different team than the link named.
 */
export const teamMatchesId = (
  team: StartTeam | null | undefined,
  teamId: string | null | undefined
): boolean => {
  if (!teamId || !team) {
    return false
  }
  return team.uid === teamId || team.id === teamId
}

/**
 * The team's image URL, from either shape.
 *
 * Returns undefined when there is none, so the caller can pair it with
 * `resolveAssetUrl` and a placeholder without special-casing the shape.
 */
export const startTeamImageUrl = (
  team: StartTeam | null | undefined
): string | { full_path?: string | null } | undefined => {
  const image = team?.image
  if (!image) {
    return undefined
  }
  return image
}

/**
 * A draft/bucket rather than a real team, which gets its own icon.
 *
 * Two payloads spell this differently — `is_registration_bucket` on the
 * top-level `teams[]`, `is_bucket` on the team endpoints — so this reads either
 * rather than making every caller know which list it came from.
 */
export const isDraftTeam = (team: StartTeam | null | undefined): boolean =>
  Boolean(team?.is_bucket || team?.is_registration_bucket)

/**
 * The roster cap that applies to this team.
 *
 * A bucket is capped by `max_players_per_bucket`; a real team by
 * `max_players_per_team`. Picking the wrong one would show a draft measured
 * against a limit that does not govern it.
 */
export const teamMaxPlayers = (
  team: StartTeam | null | undefined
): number | null => {
  if (!team) {
    return null
  }
  const max = isDraftTeam(team)
    ? team.max_players_per_bucket
    : team.max_players_per_team
  return typeof max === 'number' ? max : null
}

/**
 * "4 / 30 Players Registered", "4 Players Registered", or null when the count
 * is unknown.
 *
 * The cap comes from `teamMaxPlayers`, which picks `max_players_per_bucket` for
 * a draft and `max_players_per_team` for a real team — so a card never measures
 * one against the other's limit. An uncapped team (both null, as the API sends
 * for an unlimited division) drops the "/ max" half rather than inventing one.
 *
 * Null rather than "0 Players Registered" for a team whose payload omits the
 * count — the trimmed nested shape has no roster data at all, and claiming an
 * empty roster would be wrong.
 */
export const teamRosterLabel = (
  team: StartTeam | null | undefined
): string | null => {
  const count = team?.player_count
  if (typeof count !== 'number') {
    return null
  }
  const max = teamMaxPlayers(team)
  return max != null
    ? `${count} / ${max} Players Registered`
    : `${count} Players Registered`
}

/**
 * Why a full team cannot be picked.
 *
 * One string for every surface that has to say it — the card tooltip, the list
 * row, and the modal shown when a `?team_id=` link names a full team — so the
 * three cannot drift into three different explanations of one rule.
 */
export const TEAM_FULL_MESSAGE =
  'This team is full and cannot accept more registrations.'

/**
 * The same rule, phrased to follow a team name: "Free Birds, this team is
 * full…".
 *
 * Derived from `TEAM_FULL_MESSAGE` rather than written out again, so the two
 * cannot drift — only the leading capital differs, which is a matter of where
 * the sentence starts, not of what it says. Falls back to the standalone form
 * when the name is unknown, so the notice never opens on a stray comma.
 */
export const teamFullMessageFor = (
  teamName: string | null | undefined
): string => {
  if (!teamName) {
    return TEAM_FULL_MESSAGE
  }
  const continued =
    TEAM_FULL_MESSAGE.charAt(0).toLowerCase() + TEAM_FULL_MESSAGE.slice(1)
  return `${teamName}, ${continued}`
}

/**
 * Whether the roster is known to be full.
 *
 * Only true when both numbers are present — an unknown count must not read as
 * full, or a perfectly open team becomes unpickable.
 */
export const isTeamRosterFull = (
  team: StartTeam | null | undefined
): boolean => {
  const count = team?.player_count
  const max = teamMaxPlayers(team)
  if (typeof count !== 'number' || max == null) {
    return false
  }
  return count >= max
}
